import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X, Loader2, AlertTriangle, SwitchCamera } from "lucide-react";
import { toast } from "sonner";

/**
 * BarcodeCameraScanner
 * Props:
 *  - onDetected(code): kod okunduğunda çağrılır
 *  - autoStart (bool): mount olduğunda kamerayı otomatik başlat
 *  - onClose(): X butonuna basınca / modal kapatma (opsiyonel)
 *  - fullScreen (bool): modal/tam ekran görünüm
 *  - className
 */
export default function BarcodeCameraScanner({
  onDetected,
  autoStart = false,
  onClose,
  fullScreen = false,
  className = "",
}) {
  const [active, setActive] = useState(!!autoStart);
  const [starting, setStarting] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState("");
  const [error, setError] = useState("");
  const [retryTick, setRetryTick] = useState(0);
  const scannerRef = useRef(null);
  const readerId = useRef(`camscan-${Math.random().toString(36).slice(2)}`);
  const lastCodeRef = useRef({ code: "", at: 0 });
  const containerRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    let mounted = true;
    setError("");
    setStarting(true);

    // Force-release any lingering camera streams (fixes "camera in use elsewhere" on Android Chrome)
    const releaseStaleStream = async () => {
      try {
        document.querySelectorAll("video").forEach(v => {
          try {
            const s = v.srcObject;
            if (s && s.getTracks) s.getTracks().forEach(t => t.stop());
            v.srcObject = null;
            v.pause?.();
          } catch (_) {}
        });
      } catch (_) {}
    };

    const MAX_ATTEMPTS = 3;

    const tryStart = async (attempt = 0) => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Bu tarayıcı kamerayı desteklemiyor");
        }

        // Hızlı yol (attempt=0): direkt facingMode ile başla — getCameras() çağırma, stale cleanup yapma.
        // Bu Android'de gecikmeyi ~1-2 sn azaltıyor.
        // Fallback (attempt>=1): stream temizle + gecikme
        if (attempt > 0) {
          await releaseStaleStream();
          await new Promise(r => setTimeout(r, 300 * attempt));
        }

        // Guard: stop any leftover Html5Qrcode instance in the reader div
        try {
          if (scannerRef.current) {
            await scannerRef.current.stop().catch(() => {});
            try { scannerRef.current.clear(); } catch (_) {}
            scannerRef.current = null;
          }
        } catch (_) {}

        const scanner = new Html5Qrcode(readerId.current, { verbose: false });
        scannerRef.current = scanner;

        const box = () => {
          const w = containerRef.current?.clientWidth || 320;
          const side = Math.min(280, Math.max(180, Math.floor(w * 0.75)));
          return { width: side, height: Math.floor(side * 0.6) };
        };

        // 3 stratejik başlatma:
        // 1) İlk denemede cameraId varsa onu, yoksa facingMode "environment" (düz string!) ile başla.
        //    html5-qrcode `{ideal:...}` MediaTrackConstraint formatını KABUL ETMİYOR;
        //    sadece düz string ya da `{exact:...}` object destekliyor.
        // 2) Son denemede deviceId kilitli kaldıysa facingMode ile deneme.
        const startTarget = attempt >= 2
          ? { facingMode: "environment" }
          : (cameraId || { facingMode: "environment" });

        await scanner.start(
          startTarget,
          { fps: 12, qrbox: box, aspectRatio: 1.333, disableFlip: false },
          (decoded) => {
            const now = Date.now();
            if (lastCodeRef.current.code === decoded && now - lastCodeRef.current.at < 1500) return;
            lastCodeRef.current = { code: decoded, at: now };
            try { navigator.vibrate?.(80); } catch (_) {}
            onDetected?.(decoded);
          },
          () => { /* frame hatalarını yok say */ }
        );
        if (mounted) {
          setStarting(false);
          setError("");
        }

        // Kamera listesi (switch tuşu için) — arka planda ve sadece bir kez yükle
        if (cameras.length === 0) {
          Html5Qrcode.getCameras()
            .then(devs => { if (mounted && devs?.length) setCameras(devs); })
            .catch(() => {});
        }
      } catch (e) {
        if (!mounted) return;
        const isBusy = e?.name === "NotReadableError" || /in use|already|başka|track start|could not start/i.test(e?.message || "");
        // Auto-retry up to MAX_ATTEMPTS-1 times if NotReadableError (camera busy from previous session)
        if (isBusy && attempt < MAX_ATTEMPTS - 1) {
          console.warn(`Kamera meşgul (deneme ${attempt + 1}/${MAX_ATTEMPTS}), yeniden deneniyor...`, e);
          setError(`Kamera hâlâ kilitli — otomatik yeniden deneniyor (${attempt + 2}/${MAX_ATTEMPTS})...`);
          setTimeout(() => tryStart(attempt + 1), 700 + attempt * 300);
          return;
        }
        const msg = (e?.name === "NotAllowedError" || /permission|denied/i.test(e?.message || ""))
          ? "Kamera izni verilmedi. Chrome adres çubuğundaki kilit ikonuna dokunun → İzinler → Kamera → İzin Ver, sonra sayfayı yenileyin."
          : (e?.name === "NotFoundError")
            ? "Kamera bulunamadı. Cihazda kamera olduğundan emin olun."
            : isBusy
              ? "Kamera başlatılamadı — cihaz kamerayı serbest bırakmıyor. Çözüm: (1) Chrome'da sağ üstteki 3 nokta → Ayarlar → Site Ayarları → Kamera'dan bu siteye izin verildiğini kontrol edin. (2) Chrome'u tamamen kapatın (son kullanılanlardan silin) ve tekrar açın. (3) Telefonu yeniden başlatın. (4) Aşağıdaki 'Yeniden Dene' tuşuna basın."
              : "Kamera başlatılamadı: " + (e?.message || e);
        setError(msg);
        toast.error(msg);
        setStarting(false);
        // Not: setActive(false) YAPMIYORUZ — kullanıcı "Yeniden Dene" tuşuna basabilsin
      }
    };

    tryStart(0);

    return () => {
      mounted = false;
      const s = scannerRef.current;
      if (s) {
        try {
          s.stop().catch(() => {}).finally(() => {
            try { s.clear && s.clear(); } catch (_) {}
            // Extra safety: explicitly stop any remaining tracks
            try {
              const videoEl = document.getElementById(readerId.current)?.querySelector("video");
              const stream = videoEl?.srcObject;
              if (stream && stream.getTracks) stream.getTracks().forEach(t => t.stop());
            } catch (_) {}
          });
        } catch (_) {}
        scannerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, cameraId, retryTick]);

  const retry = async () => {
    setError("");
    // Zorla stream'leri temizle
    try {
      document.querySelectorAll("video").forEach(v => {
        try {
          const s = v.srcObject;
          if (s && s.getTracks) s.getTracks().forEach(t => t.stop());
          v.srcObject = null;
        } catch (_) {}
      });
    } catch (_) {}
    setRetryTick(t => t + 1);
  };

  const switchCamera = () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex(c => c.id === cameraId);
    const next = cameras[(idx + 1) % cameras.length];
    setCameraId(next.id);
  };

  const close = () => {
    setActive(false);
    onClose?.();
  };

  // Tam ekran / modal görünüm
  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-[70] bg-black flex flex-col" data-testid="camera-modal">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white shrink-0">
          <div className="flex items-center gap-2 font-bold text-sm">
            <Camera size={18}/> Barkod Tara
          </div>
          <div className="flex items-center gap-2">
            {cameras.length > 1 && (
              <button onClick={switchCamera} data-testid="camera-switch-btn"
                className="p-2 border border-slate-700 rounded"
                aria-label="Kamera değiştir">
                <SwitchCamera size={18}/>
              </button>
            )}
            <button onClick={close} data-testid="camera-close-btn"
              className="p-2 border border-slate-700 rounded"
              aria-label="Kapat">
              <X size={18}/>
            </button>
          </div>
        </div>
        <div className="flex-1 relative bg-black overflow-hidden" ref={containerRef}>
          <div
            id={readerId.current}
            data-testid="camera-viewport"
            className="w-full h-full"
            style={{ minHeight: 320 }}
          />
          {starting && (
            <div className="absolute inset-0 grid place-items-center text-white">
              <div className="flex flex-col items-center gap-2">
                <Loader2 size={32} className="animate-spin"/>
                <div className="text-sm">Kamera başlatılıyor...</div>
              </div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 grid place-items-center bg-black/90 text-white p-6">
              <div className="max-w-sm text-center">
                <AlertTriangle size={40} className="mx-auto mb-3 text-amber-400"/>
                <div className="text-sm whitespace-pre-line">{error}</div>
                <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
                  <button onClick={retry} data-testid="camera-retry-btn"
                    className="px-4 py-2 bg-amber-400 text-slate-900 font-bold text-sm">
                    Yeniden Dene
                  </button>
                  <button onClick={close}
                    className="px-4 py-2 bg-white text-slate-900 font-bold text-sm">
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-3 bg-slate-900 text-slate-300 text-xs text-center shrink-0">
          Barkodu kutunun içinde tutun — otomatik okunur
        </div>
      </div>
    );
  }

  // Gömülü (inline) görünüm
  return (
    <div className={"card-industrial p-4 " + className}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-slate-600">
          <Camera size={14} /> Kameradan Oku
        </div>
        <div className="flex items-center gap-2">
          {active && cameras.length > 1 && (
            <button onClick={switchCamera} data-testid="camera-switch"
              className="p-1.5 border border-slate-300 hover:border-slate-800" aria-label="Kamera değiştir">
              <SwitchCamera size={14}/>
            </button>
          )}
          <button
            type="button"
            onClick={() => setActive(!active)}
            data-testid="camera-toggle"
            className={`inline-flex items-center gap-1 px-3 py-1.5 border-2 font-bold text-xs uppercase ${active ? "border-red-600 text-red-700 bg-red-50" : "border-slate-800 text-slate-800 bg-white hover:bg-slate-900 hover:text-white"}`}
          >
            {active ? <><X size={14}/> Kapat</> : <><Camera size={14}/> Kamerayı Aç</>}
          </button>
        </div>
      </div>

      {!active && !error && (
        <div className="text-xs text-slate-500 text-center py-3">
          Kamera ile barkod okutmak için "Kamerayı Aç" tuşuna basın. Tarayıcı kamera izni isteyecek.
        </div>
      )}

      {error && !active && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-300 p-3 flex items-start gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5"/>
          <div className="flex-1 whitespace-pre-line">{error}</div>
        </div>
      )}
      {error && active && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-300 p-3 flex flex-col gap-2 mb-2">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5"/>
            <div className="flex-1 whitespace-pre-line">{error}</div>
          </div>
          <button onClick={retry} data-testid="camera-retry-btn-inline"
            className="self-start px-3 py-1.5 bg-amber-400 text-slate-900 font-bold text-xs uppercase">
            Yeniden Dene
          </button>
        </div>
      )}

      <div
        ref={containerRef}
        className={active ? "relative w-full max-w-md mx-auto border-2 border-slate-800 overflow-hidden bg-black" : "hidden"}
        style={active ? { minHeight: 260 } : undefined}
      >
        <div
          id={readerId.current}
          data-testid="camera-viewport"
          style={{ minHeight: 260 }}
          className="w-full"
        />
        {starting && (
          <div className="absolute inset-0 grid place-items-center text-white">
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={28} className="animate-spin"/>
              <div className="text-xs">Kamera başlatılıyor...</div>
            </div>
          </div>
        )}
      </div>

      {active && !error && (
        <div className="mt-2 text-[11px] text-center text-slate-500">
          Barkod otomatik okunur — okuma sonrasında kamera açık kalır (arka arkaya birden fazla parça okutabilirsiniz).
        </div>
      )}
    </div>
  );
}
